import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  useTheme,
  Chip,
  Stack,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
} from '@mui/material';
import {
  Router,
  CellTower,
  Storage,
  Wifi,
  Analytics,
  Cloud,
  CheckCircle,
  Speed,
  Security,
  Public,
  Language,
  ArrowForward,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const Services = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);

  const services = [
   
    {
      id: 0,
      title: 'Telecom Towers',
      icon: <CellTower sx={{ fontSize: 50 }} />,
      description: 'Extensive tower infrastructure for seamless wireless coverage.',
      longDesc: 'Our network of 10,000+ towers provides critical infrastructure for mobile operators and wireless ISPs.',
      features: [
        '10,000+ towers worldwide',
        '5G ready infrastructure',
        '24/7 security and surveillance',
        'Backup power systems',
        'Climate-controlled equipment shelters',
        'Remote monitoring capabilities',
      ],
      stats: { towers: '10,000+', coverage: '98%', technology: '5G Ready' },
      color: '#00A3FF',
    },
    {
      id: 1,
      title: 'Data Centers',
      icon: <Storage sx={{ fontSize: 50 }} />,
      description: 'Secure, scalable data center solutions with global presence.',
      longDesc: 'Our 50+ data centers across the globe provide enterprise-grade colocation and cloud connectivity services.',
      features: [
        'Colocation services (cage, cabinet, custom)',
        'Bare metal servers',
        'Cloud connect solutions',
        'Disaster recovery as a service',
        'Data backup and archiving',
        'Carrier-neutral connectivity',
      ],
      stats: { locations: '50+', uptime: '99.999%', security: 'Tier III+' },
      color: '#00C853',
    },
    {
      id: 2,
      title: 'Internet Solutions',
      icon: <Wifi sx={{ fontSize: 50 }} />,
      description: 'Reliable high-speed internet for businesses of all sizes.',
      longDesc: 'From dedicated internet access to SD-WAN solutions, we provide tailored connectivity for modern enterprises.',
      features: [
        'Dedicated Internet Access',
        'Broadband solutions',
        'SD-WAN services',
        '4G/5G backup',
        'Managed WiFi',
        'Business continuity solutions',
      ],
      stats: { speed: 'Up to 100Gbps', coverage: 'Global', support: '24/7' },
      color: '#FFC107',
    },
    {
      id: 3,
      title: 'Managed Services',
      icon: <Analytics sx={{ fontSize: 50 }} />,
      description: '24/7 IT infrastructure management and support.',
      longDesc: 'Let our experts handle your infrastructure while you focus on growing your business.',
      features: [
        'Network management',
        'Security Operations Center (SOC)',
        'Cloud management',
        'IT help desk',
        'Compliance management',
        'Performance optimization',
      ],
      stats: { monitoring: '24/7', response: '<15min', certified: 'ISO 27001' },
      color: '#FF6B00',
    },
    {
      id: 4,
      title: 'Enterprise Solutions',
      icon: <Cloud sx={{ fontSize: 50 }} />,
      description: 'Custom solutions for global enterprises.',
      longDesc: 'Tailored communication infrastructure designed for large-scale global operations.',
      features: [
        'Global MPLS network',
        'Unified communications',
        'IoT connectivity',
        'Dedicated account team',
        'Custom SLAs',
        'Global support desk',
      ],
      stats: { clients: '500+', countries: '150+', satisfaction: '98%' },
      color: '#9C27B0',
    },
  ];

  const currentService = services[selectedTab];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
          color: 'white',
          py: 10,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Chip
              label="Our Services"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', mb: 3 }}
            />
            <Typography variant="h1" sx={{ mb: 3, fontWeight: 800, fontSize: { xs: '2.5rem', md: '4rem' } }}>
              Comprehensive Communication
              <br />
              Solutions
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: 600 }}>
              From fiber infrastructure to managed services, we provide end-to-end solutions
              for businesses of all sizes.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Services Grid */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="xl">
          <Grid container spacing={4}>
            {/* Service Tabs */}
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  position: 'sticky',
                  top: 100,
                  borderRadius: 3,
                  overflow: 'hidden',
                }}
              >
                <Tabs
                  orientation="vertical"
                  value={selectedTab}
                  onChange={(e, v) => setSelectedTab(v)}
                  sx={{
                    borderRight: 1,
                    borderColor: 'divider',
                    '& .MuiTab-root': {
                      alignItems: 'flex-start',
                      textAlign: 'left',
                      py: 2,
                      px: 3,
                    },
                  }}
                >
                  {services.map((service) => (
                    <Tab
                      key={service.id}
                      label={
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Box sx={{ color: service.color }}>{service.icon}</Box>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {service.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {service.description.substring(0, 50)}...
                            </Typography>
                          </Box>
                        </Stack>
                      }
                    />
                  ))}
                </Tabs>
              </Paper>
            </Grid>

            {/* Service Details */}
            <Grid item xs={12} md={8}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={selectedTab}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <Box
                      sx={{
                        background: `linear-gradient(135deg, ${currentService.color}20 0%, ${currentService.color}05 100%)`,
                        p: 4,
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                        <Avatar
                          sx={{
                            width: 80,
                            height: 80,
                            bgcolor: currentService.color,
                            color: 'white',
                          }}
                        >
                          {currentService.icon}
                        </Avatar>
                        <Box>
                          <Typography variant="h3" fontWeight="bold">
                            {currentService.title}
                          </Typography>
                          <Typography variant="body1" color="text.secondary">
                            {currentService.longDesc}
                          </Typography>
                        </Box>
                      </Stack>

                      {/* Stats */}
                      <Grid container spacing={2} sx={{ mb: 4 }}>
                        {Object.entries(currentService.stats).map(([key, value]) => (
                          <Grid item xs={4} key={key}>
                            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper' }}>
                              <Typography variant="h5" fontWeight="bold" sx={{ color: currentService.color }}>
                                {value}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" textTransform="capitalize">
                                {key}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>

                      {/* Features */}
                      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
                        Key Features
                      </Typography>
                      <Grid container spacing={2}>
                        {currentService.features.map((feature, idx) => (
                          <Grid item xs={12} sm={6} key={idx}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <CheckCircle sx={{ color: currentService.color }} />
                              <Typography variant="body2">{feature}</Typography>
                            </Stack>
                          </Grid>
                        ))}
                      </Grid>

                      <Button
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForward />}
                        onClick={() => navigate('/contact')}
                        sx={{
                          mt: 4,
                          background: `linear-gradient(135deg, ${currentService.color} 0%, ${currentService.color}cc 100%)`,
                        }}
                      >
                        Get This Service
                      </Button>
                    </Box>
                  </Card>
                </motion.div>
              </AnimatePresence>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Services;